import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Image from "material-ui-image";
import { makeStyles } from "@material-ui/core/styles";
import Container from "@material-ui/core/Container";
import TableContainer from "@material-ui/core/TableContainer";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import Checkbox from "@material-ui/core/Checkbox";
import Box from "@material-ui/core/Box";
import TablePagination from "@material-ui/core/TablePagination";
import DeleteIcon from "@material-ui/icons/Delete";
import IconButton from "@material-ui/core/IconButton";
import EditIcon from "@material-ui/icons/Edit";
import EnhancedTableHead from "../../components/common/table/EnhancedTableHead";
import RecordChangeButtons from "../../components/common/recordChangeButtons/RecordChangeButtons";
import CustomSpinner from "../../components/common/Spinner/CustomSpinner";
import { getComparator, stableSort } from "../../utils/tableFilter";
import Layout from "../../components/common/hoc/Layout";
import NotFound from "../../components/common/notFound/NotFound";
import {
  myActivities,
  deleteSingleActivity,
  deleteMultipleActivity,
} from "../../store/duck/myActivities/myActivities";
import { showCustomConfirmationDialog } from "../../store/customConfirmationDialogReducer";
import { showSnackbar } from "../../store/duck/uiReducer/uiReducer";

const useStyles = makeStyles((theme) => ({
  table: {
    marginTop: "1rem",
    minWidth: 750,
  },
  buttons: {
    display: "flex",
    justifyContent: "flex-start",
    width: "100%",
  },
}));

const MyActivities = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation("translation");
  const myContentList = useSelector((state) => state.myActivities);
  const { loading, error, interactions } = myContentList;
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("");
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const headCells = [
    {
      id: "contentName",
      numeric: false,
      disablePadding: false,
      label: t("myActivities.headCells.contentName"),
    },
    {
      id: "schoolClass",
      numeric: false,
      disablePadding: false,
      label: t("myActivities.headCells.schoolClass"),
    },
    {
      id: "schoolGrade",
      numeric: false,
      disablePadding: false,
      label: t("myActivities.headCells.schoolGrade"),
    },
    {
      id: "subject",
      numeric: false,
      disablePadding: false,
      label: t("myActivities.headCells.subject"),
    },
    {
      id: "createdAt",
      numeric: false,
      disablePadding: false,
      label: t("myActivities.headCells.createdAt"),
    },
  ];
  const isSelected = (name) => selected.indexOf(name) !== -1;
  const emptyRows =
    rowsPerPage -
    Math.min(rowsPerPage, interactions?.length - page * rowsPerPage);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = interactions.map((n) => n);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, name) => {
    const selectedIndex = selected.indexOf(name);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const deleteSingle = async (activity) => {
    const configDialog = {
      title: t("myActivities.deleteDialog.title"),
      message: `${t("myActivities.deleteDialog.firstMessage")} "${
        activity.lectureName
      }", ${t("myActivities.deleteDialog.secondMessage")}`,
      onSubmit: async function () {
        await dispatch(deleteSingleActivity(activity.id))
          .then(async (result) => {
            await dispatch({ type: "CUSTOM_CONFIRMATION_DIALOG_CLOSED" });
            await dispatch(
              showSnackbar({
                message: "Atividade removida com sucesso!",
                severity: "success",
                autoHideDuration: 3000,
              })
            );
            await dispatch(myActivities());
            setSelected([]);
          })
          .catch(async (err) => {
            await dispatch(
              showSnackbar({
                message: "Ocorreu um erro ao remover a atividade!",
                severity: "error",
                autoHideDuration: 3000,
              })
            );
          });
      },

      close: async function () {
        await dispatch({ type: "CUSTOM_CONFIRMATION_DIALOG_CLOSED" });
      },
      buttons: {
        cancel: t("myActivities.deleteDialog.buttons.cancel"),
        confirm: t("myActivities.deleteDialog.buttons.confirm"),
      },
    };

    dispatch(
      showCustomConfirmationDialog({
        ...configDialog,
      })
    );
  };

  const deleteMultiple = async (activities) => {
    const configDialog = {
      title: t("myActivities.deleteDialog.title"),
      message: `${t("myActivities.deleteDialog.firstMessage")} "${
        activities[0].lectureName
      }", ${t("myActivities.deleteDialog.secondMessage")}`,
      onSubmit: async function () {
        await dispatch(deleteMultipleActivity(activities))
          .then(async (result) => {
            await dispatch({ type: "CUSTOM_CONFIRMATION_DIALOG_CLOSED" });
            await dispatch(
              showSnackbar({
                message: "Atividades removidas com sucesso!",
                severity: "success",
                autoHideDuration: 3000,
              })
            );
            await dispatch(myActivities());
            setSelected([]);
          })
          .catch(async (err) => {
            await dispatch(
              showSnackbar({
                message: "Ocorreu um erro ao remover as atividades!",
                severity: "error",
                autoHideDuration: 3000,
              })
            );
          });
      },

      close: async function () {
        await dispatch({ type: "CUSTOM_CONFIRMATION_DIALOG_CLOSED" });
      },
      buttons: {
        cancel: t("myActivities.deleteDialog.buttons.cancel"),
        confirm: t("myActivities.deleteDialog.buttons.confirm"),
      },
    };

    dispatch(
      showCustomConfirmationDialog({
        ...configDialog,
      })
    );
  };

  const formatDate = (date) => {
    return moment(date).format("dddd - DD/MM/YYYY");
  };

  const getDiscipline = (discipline) => {
    /* retornos
      O backend retorna uma string simples se tiver uma materia cadastrada:
      Ex:
      'Biologia'

      Se tiver mais de uma materia cadastrada o retorno é um array de strings:
      Ex:
      '['Biologia', 'Física']'

      Para fazer o parse do JSON, é necessário usar Try catch para não causar erro de execução.
      O ideal seria sempre retornar um array, mesmo que tenha apenas uma matéria cadastrada.
      Para não ter que escrever código 

    */
    try {
      const disciplines = JSON.parse(discipline);
      const result = disciplines.map((discipline) => {
        return discipline.title;
      });
      if (result.length === 0) return result;

      return result.join(" - ");
    } catch (error) {
      return discipline;
    }
  };

  const getSchoolClass = (schoolClasses) => {
    const tempSchoolClasses = JSON.parse(schoolClasses);
    const result = tempSchoolClasses.map((schoolClass) => {
      return schoolClass;
    });

    return result.join(" - ");
  };

  const formConfigI18n = {
    singleDeleteButton: t("common.singleDeleteButton"),
    singleDeleteMultipleButton: t("common.singleDeleteMultipleButton"),
  };

  useEffect(() => {
    dispatch(myActivities());
  }, [dispatch]);

  return (
    <Layout title={t("myActivities.title")}>
      <Container disableGutters maxWidth={false}>
        <>
          {loading ? (
            <CustomSpinner />
          ) : error ? (
            <NotFound
              title="Oops!"
              message="Ocorreu um erro, tente recarregar a página"
              image="/images/notfound.png"
              redirect="/app/content"
            />
          ) : (
            <>
              <TableContainer>
                <Table
                  className={classes.table}
                  aria-labelledby="tableTitle"
                  size={"medium"}
                  aria-label="enhanced table"
                >
                  <EnhancedTableHead
                    headCells={headCells}
                    classes={classes}
                    numSelected={selected.length}
                    order={order}
                    orderBy={orderBy}
                    onSelectAllClick={handleSelectAllClick}
                    onRequestSort={handleRequestSort}
                    rowCount={interactions.length}
                  />
                  <TableBody>
                    {stableSort(interactions, getComparator(order, orderBy))
                      .slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage
                      )
                      .map((row, index) => {
                        const isItemSelected = isSelected(row);
                        const labelId = `enhanced-table-checkbox-${index}`;

                        return (
                          <TableRow
                            hover
                            role="checkbox"
                            aria-checked={isItemSelected}
                            tabIndex={0}
                            key={row.id}
                            selected={isItemSelected}
                          >
                            <TableCell align="left" padding="checkbox">
                              <Checkbox
                                onClick={(event) => handleClick(event, row)}
                                checked={isItemSelected}
                                inputProps={{ "aria-labelledby": labelId }}
                              />

                              {/* <Link
                                to={`/app/my-content/update-content/${row.idContent}`}
                              >
                                <IconButton aria-label="edit">
                                  <EditIcon color="secondary" />
                                </IconButton>
                              </Link> */}
                            </TableCell>

                            <TableCell align="left">
                              {row.lectureName}
                            </TableCell>
                            <TableCell align="left">
                              {getSchoolClass(row.turmas)}
                            </TableCell>
                            <TableCell align="left">{row.serie}</TableCell>
                            <TableCell align="left">
                              {getDiscipline(row.discipline)}
                            </TableCell>
                            <TableCell align="left">
                              {formatDate(row.createdAt)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    {emptyRows > 0 && (
                      <TableRow style={{ height: 53 * emptyRows }}>
                        <TableCell colSpan={6} />
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box display="flex" p={1}>
                <Box p={1} flexGrow={1}>
                  <TablePagination
                    rowsPerPageOptions={[10, 25]}
                    component="div"
                    count={interactions.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onChangePage={handleChangePage}
                    onChangeRowsPerPage={handleChangeRowsPerPage}
                  />
                </Box>
                <Box p={1} flexGrow={1}>
                  <RecordChangeButtons
                    singleDeleteButton={formConfigI18n.singleDeleteButton}
                    singleDeleteMultipleButton={
                      formConfigI18n.singleDeleteMultipleButton
                    }
                    itensSelected={selected}
                    handlerSingle={deleteSingle}
                    handlerMultiple={deleteMultiple}
                    icon={<DeleteIcon />}
                  />
                </Box>
              </Box>
            </>
          )}
        </>
      </Container>
    </Layout>
  );
};

export { MyActivities };
